import { createElement as rc, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { constants, metadata } from 'lib_ui-services';
import { hooks } from 'lib_ui-primitives';
import { MenuItem } from '../menuSelection';
import MenuContext from '../menuSelection/MenuContext';
import Button from './Button';
import DropDownButton from '../_abstractComponent/DropDownButton';
import { useNetwork, useNavigationSelection, useMultiSelect } from '../../hooks';
import useGetDbViewName from '../../hooks/useDbView/useGetDbViewName';
import { _private as useColumnCustomization } from '../list/useColumnCustomization';
import useExportingState from '../../hooks/useExportingState';

const { useBbState, useRouter } = hooks;

const _p = {
    useBbState,
    useRouter,
    useGetDbViewName,
    useDisplayedColumns,
    getCustomizationStateName: useColumnCustomization.getCustomizationStateName,
    getRelationMetadata: metadata.getRelationMetadata
};
export const _private = _p;

const exportTypes = [
    { title: 'Excel 2007+ XML (XLSX)', type: 'xlsx' },
    { title: 'Excel 97-2004 Workbook (XLS)', type: 'xls' },
    { title: 'Comma Separated Values (CSV)', type: 'csv' },
    { title: 'HTML Document', type: 'html' },
    { title: 'PDF Document', type: 'pdf' }
];

function DownloadButton(props) {
    const {
        hNode: { iconName = 'cloud_download', includeExportType, namespace, relation, children, id, filePrefix },
        data
    } = props || { hNode: {} };

    // Check if we're inside a menu context (SubMenu)
    const menuContext = useContext(MenuContext);
    const isInsideMenu = menuContext?.isInsideMenu || menuContext === true; // Support both old and new context format
    const viewName = _p.useGetDbViewName(namespace, relation, props.viewName);
    const { isConnected } = useNetwork();
    const { limitSyncSize } = _p.getRelationMetadata(namespace, relation);
    const isExporting = useExportingState(namespace, relation);

    // Get the navigation selection context to include in the export if needed
    const navigationSelection = useNavigationSelection();

    // Get the current selection from the multi-select context (for exporting only selected items)
    const { selection } = useMultiSelect({ namespace, relation });

    // Get the displayed columns for the export
    const displayedColumns = _p.useDisplayedColumns(namespace, relation, children);
    const propsTitle = props.title || props.hNode?.title;
    const altText = includeExportType ? propsTitle || 'Select Format to Export To' : propsTitle || 'Export Data';

    const actionPayload = {
        //the file name prefix
        filePrefix,
        //to allow retrieving the view when generating the export
        viewName,
        //just in case the export data was passed in explicitly (e.g. from charts)
        data,
        //what properties to include in the export
        columns: displayedColumns,
        //the navigation selection context (for multi-inventory exports)
        navigationSelection,
        // the current selection (for exporting only selected items)
        selection,
        // needed for the export to know how to fetch the data
        limitSyncSize,
        // the network status
        isConnected
    };

    const { variant, size, buttonStyle } = props.hNode || {};

    // Common props for Button/DropDownButton (standalone mode)
    const commonProps = {
        id,
        alt: altText,
        hoverText: altText,
        variant,
        text: propsTitle,
        size,
        buttonStyle,
        ...props,
        ...(includeExportType
            ? {}
            : {
                  iconName,
                  forAction: 'export'
              }),
        actionPayload
    };

    // If inside a menu context, render as MenuItem(s)
    if (isInsideMenu) {
        // Note: Do NOT use withSelection for export MenuItems.
        // withSelection changes verb to 'selection' which is for actions like mass delete.
        // Export needs verb='export' and type='csv'/'xlsx'/etc.
        // The selection info is already in actionPayload for filtering.

        if (includeExportType) {
            // Render MenuItem children for export type selection
            // The parent SubMenu provides the submenu wrapper
            return rc(
                Fragment,
                null,
                exportTypes.map(type =>
                    rc(MenuItem, {
                        key: type.type,
                        hNode: {
                            forAction: 'export',
                            title: type.title,
                            namespace,
                            relation,
                            type: type.type
                        },
                        actionPayload: {
                            ...actionPayload,
                            type: type.type
                        },
                        disabled: isExporting
                    })
                )
            );
        }

        // For single export (no type selection), render as a MenuItem
        return rc(MenuItem, {
            hNode: {
                id,
                forAction: 'export',
                title: altText,
                iconName,
                namespace,
                relation
            },
            actionPayload,
            ...props,
            disabled: isExporting
        });
    }

    // Standalone mode (not inside a menu) - render as Button or DropDownButton
    if (includeExportType) {
        return rc(
            DropDownButton,
            { ...commonProps, icon: iconName, disabled: isExporting },
            exportTypes.map(type =>
                rc(DropDownButton.MenuItem, {
                    hNode: { forAction: 'export', namespace, relation, ...type },
                    actionPayload: {
                        ...actionPayload,
                        type: type.type
                    },
                    key: type.type,
                    disabled: isExporting
                })
            )
        );
    }

    return rc(Button, {
        ...commonProps,
        hNode: { ...props.hNode, disabled: props.hNode?.disabled || isExporting }
    });
}

/**
 * Get the displayed columns for the export
 * @param {string} namespace
 * @param {string} relation
 * @param {Array} columns
 * @returns {Array} The displayed columns for the export with their sequence and hidden properties
 */
function useDisplayedColumns(namespace, relation, columns) {
    const router = _p.useRouter();
    const pathname = _p.getCustomizationStateName(router.location.pathname, namespace, relation);

    const [storedColumnHNodes] = _p.useBbState(columns, pathname, {
        duration: constants.retention.DURATION.ALWAYS,
        scope: constants.retention.SCOPE.GLOBAL,
        visibility: constants.retention.VISIBILITY.PER_BROWSER
    });

    return columns.map(column => {
        // Find the stored column that matches the current column's title
        const storedColumnIndex = storedColumnHNodes.findIndex(sc => sc.title === column.title);
        const storedColumn = storedColumnHNodes[storedColumnIndex];
        // We need the stored column's index (for the sequence) and hidden property
        // If a stored column is found, merge the stored column with the current column
        if (storedColumn) {
            return { ...column, ...storedColumn, sequence: storedColumnIndex };
        }
        return column;
    });
}

DownloadButton.propTypes = {
    hNode: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        namespace: PropTypes.string,
        relation: PropTypes.string,
        /** whether or not to display export type dropdown */
        includeExportType: PropTypes.bool,
        /** OPTIONAL: if undefined, prettyRelationNme or `export_{relation}` will be used */
        filePrefix: PropTypes.string
    }).isRequired,
    /** OPTIONAL: to have the export pull the data directly from the lokiView */
    viewName: PropTypes.string,
    /** OPTIONAL: to pass data in directly */
    data: PropTypes.array
};

export default DownloadButton;
